import React, { FC, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import {
  FInter,
  FlexBox,
  MagicBox,
  useBreakpoint,
  Colors,
} from "@brg/ui";
import { getCookie, setCookie } from "../../helpers/cookies";
import { ReactComponent as CheckIcon } from "../../assets/check.svg";
import { LayoutConst } from "core";
import { CloseIcon } from "shared/icons";
import useAppTester from "../../MobileMenu/hooks/useAppTester";
import { useTranslation } from "react-i18next";

type MagicBoxRowPropsT = {
  onClick: (ev: React.SyntheticEvent) => void;
  checked: boolean;
  label?: string;
  style?: any;
  tip?: string;
};

export const CheckboxRoot = styled.div<{
  checked?: boolean;
  isGeo?: boolean;
  isContinuumSite?: boolean;
  disabled?: boolean;
}>`
  width: 24px;
  min-width: 24px;
  height: 24px;
  border: 1px solid ${(p) => (p.isGeo ? "transparent" : Colors.galBlack47)};
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(p) => (p.isGeo ? Colors.geoWhite21 : p.isContinuumSite ? Colors.zelWhite10  : Colors.gal)};
  cursor: pointer;

  ${({ disabled }) =>
    disabled
      ? css`
          background-color: #fefefe;
          border: 1px solid #b6b6b5;
          pointer-events: none;
          user-select: none;

          :hover {
            background-color: transparent;
          }
        `
      : null};

  input {
    display: none;
  }
`;

export const MagicBoxRow: FC<MagicBoxRowPropsT> = ({
  onClick,
  checked,
  label,
  style,
  tip,
}) => (
  <MagicBox
    onClick={onClick}
    style={style}
    pointer
    align="center"
    gx={10}
    data-tip={tip}
  >
    <CheckboxRoot checked={checked}>
      {checked && <CheckIcon />}
      <input checked={checked} type="checkbox" />
    </CheckboxRoot>
    <FInter.SmallTextDiv style={{ whiteSpace: "pre-wrap" }}>
      {label}
    </FInter.SmallTextDiv>
  </MagicBox>
);

export const Checkbox = styled(MagicBoxRow)`
  width: 16px !important;
  height: 16px !important;
`;

const CookiesWarningWrapper = styled(FlexBox)`
  position: fixed;
  z-index: 299999;
  bottom: 40px;
  left: 50%;
  padding: 0;
  width: 100%;
  transform: translateX(-50%);
  max-width: 700px;
  background-color: ${(p) => p.theme.cookies.bg};
  border-radius: 3px;
  flex-flow: column;
  justify-content: center;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  @media (max-width: 992px) {
    max-width: 553px;
  }
  @media (max-width: 743px) {
    max-width: 300px;
  }
`;

const CookiesInner = styled(FlexBox)<{ isDesign?: boolean }>`
  position: relative;
  padding: 11px 44px 11px 12px;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  color: ${({theme}) => theme.cookies.mainColor
};

  a {
    color: #909090;
  }

  .legals {
    text-decoration: underline;
    font-family: "Inter";
    font-weight: 400;
    cursor: pointer;
    color: #007bff;
  }

  @media (max-width: 743px) {
    padding: 8px 32px 8px 8px;
  }
`;

const CloseButton = styled.span`
  position: absolute;
  right: 15px;
  top: 15px;
  cursor: pointer;

  &:hover {
    svg path {
      fill: ${({ theme }) => theme.cookies.icon.hoverFill};
      transition: fill 0.2s;
    }
  }

  svg path {
    fill: ${({ theme }) => theme.cookies.icon.fill};
    transition: fill 0.2s;
  }

  @media (max-width: 743px) {
    top: 3px;
    right: 10px;
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const CookiesText = styled.span`
  text-align: start;
  &, a {
    display: inline;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 0;
    
    @media (max-width: 992px) {
      font-size: 13px;
      line-height: 16px;
      letter-spacing: 0;
    }

    @media (max-width: 743px) {
        font-size: 11px;
        line-height: 13px;
        letter-spacing: 0;
    }
  }
`;

const ShowMoreText = styled.div<{expanded?: boolean}>`
  ${p => !p.expanded?
    'display: inline-block;'
    :
    css`
      display: flex;
      flex-direction: column;
      justify-content: start;
    `
  }
  width: 100%;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: 0;
  margin-bottom: 0;
  
  @media (max-width: 992px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }
  @media (max-width: 743px) {
      font-size: 11px;
      line-height: 13px;
      letter-spacing: 0;
  }
`

const ShowMoreLessButton = styled.div`
  color: #909090;
  font-size: inherit;
  line-height: inherit;
  display: inline;
  cursor: pointer;
`

enum CookieType {
  HSE = "HSE",
  Deziiiign = "Deziiiign",
  None = "None",
}

interface CookieContext {
  type: CookieType;
  isCurrentContext: boolean[];
}

const CookiesWarning: FC = () => {
  const { t } = useTranslation();
  const warningSeen = getCookie("cookies_warning_seen");
  const is744 = useBreakpoint(LayoutConst.screen.s - 1);
  const [expanded, setExpanded] = useState(false)

  const [visible, setVisible] = useState(!warningSeen);

  const onClose = () => {
    setVisible(false);
    setCookie("cookies_warning_seen", true);
  };

  const {
    isHSEDesign,
    isDeziiignMain,
    isMediiia,
    isGeoMain,
    isSuperPuper,
    isAcademy,
    isBrnd,
    isDvaDesign,
    isSchool,
    isOnlines,
    isOnlineHSEDesign,
    isGalllleryArt,
    isAgeytomesh,
    isGeoDafes
  } = useAppTester();

  const cookieContextList: CookieContext[] = useMemo(
    () => [
      { type: CookieType.HSE, isCurrentContext: [isHSEDesign, isAcademy] },
      {
        type: CookieType.Deziiiign,
        isCurrentContext: [
          isDeziiignMain,
          isMediiia,
          isGeoMain,
          isSuperPuper,
          isBrnd,
          isDvaDesign,
          isSchool,
          isOnlines,
          isOnlineHSEDesign,
          isGalllleryArt,
          isAgeytomesh,
          isGeoDafes
        ],
      },
    ],
    [
      isHSEDesign,
      isDeziiignMain,
      isMediiia,
      isGeoMain,
      isSuperPuper,
      isAcademy,
      isSchool,
      isOnlines,
      isOnlineHSEDesign,
      isGalllleryArt,
      isAgeytomesh,
      isGeoDafes
    ]
  );

  const cookieType = useMemo(() => {
    return (
      cookieContextList.find((item) =>
        item.isCurrentContext.some((context) => context)
      )?.type || CookieType.None
    );
  }, [cookieContextList]);

  const cookText = useMemo(() => {
    if (cookieType === CookieType.Deziiiign) {
      return (
        expanded?
        <CookiesText className="first_child last_child ">
          {t("text.cookiesUsage")}&nbsp;
          <a
            target="_blank"
            href={`${process.env.REACT_APP_URL_DEZIIIGN}/legals/privacy`}
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          , {t("text.cookiesRules")} –&nbsp;
          <a
            target="_blank"
            href={`${process.env.REACT_APP_URL_DEZIIIGN}/legals/privacy`}
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          . {t("text.cookiesInfo")}
        </CookiesText>
        :
        <CookiesText>
          {t(is744? 'text.cookiesUsageCroppedMobile' : 'text.cookiesUsageCropped')}
        </CookiesText>
      );
    } else {
      return (
        expanded?
        <CookiesText className="first_child last_child ">
          {t("text.cookiesUsageHSE")}&nbsp;
          <a
            target="_blank"
            href="https://www.hse.ru/cookie.html"
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          , {t("text.cookiesRules")} –&nbsp;
          <a
            target="_blank"
            href="https://www.hse.ru/data_protection_regulation"
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          . {t("text.cookiesInfoHSE")}
        </CookiesText>
        :
        <CookiesText>
          {t(is744? 'text.cookiesUsageHSECroppedMobile' : 'text.cookiesUsageHSECropped')}
        </CookiesText>
      );
    }
  }, [cookieType, expanded, t, is744]);


  if (!visible || cookieType === CookieType.None) {
    return null;
  }

  return (
    <CookiesWarningWrapper>
      <CookiesInner>
        <ShowMoreText expanded={expanded}>
          {cookText}
          <ShowMoreLessButton onClick={() => setExpanded(!expanded)}>
          {
            expanded?
              ` ${t("show_less_text")}`
              :
              ` ${t("show_more_text")}`
          }
        </ShowMoreLessButton>
        </ShowMoreText>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </CookiesInner>
    </CookiesWarningWrapper>
  );
};

export default CookiesWarning;
import React, { FC, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import {
  FInter,
  FlexBox,
  MagicBox,
  useBreakpoint,
  Colors,
} from "@brg/ui";
import { getCookie, setCookie } from "../../helpers/cookies";
import { ReactComponent as CheckIcon } from "../../assets/check.svg";
import { LayoutConst } from "core";
import { CloseIcon } from "shared/icons";
import useAppTester from "../../MobileMenu/hooks/useAppTester";
import { useTranslation } from "react-i18next";

type MagicBoxRowPropsT = {
  onClick: (ev: React.SyntheticEvent) => void;
  checked: boolean;
  label?: string;
  style?: any;
  tip?: string;
};

export const CheckboxRoot = styled.div<{
  checked?: boolean;
  isGeo?: boolean;
  isContinuumSite?: boolean;
  disabled?: boolean;
}>`
  width: 24px;
  min-width: 24px;
  height: 24px;
  border: 1px solid ${(p) => (p.isGeo ? "transparent" : Colors.galBlack47)};
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(p) => (p.isGeo ? Colors.geoWhite21 : p.isContinuumSite ? Colors.zelWhite10  : Colors.gal)};
  cursor: pointer;

  ${({ disabled }) =>
    disabled
      ? css`
          background-color: #fefefe;
          border: 1px solid #b6b6b5;
          pointer-events: none;
          user-select: none;

          :hover {
            background-color: transparent;
          }
        `
      : null};

  input {
    display: none;
  }
`;

export const MagicBoxRow: FC<MagicBoxRowPropsT> = ({
  onClick,
  checked,
  label,
  style,
  tip,
}) => (
  <MagicBox
    onClick={onClick}
    style={style}
    pointer
    align="center"
    gx={10}
    data-tip={tip}
  >
    <CheckboxRoot checked={checked}>
      {checked && <CheckIcon />}
      <input checked={checked} type="checkbox" />
    </CheckboxRoot>
    <FInter.SmallTextDiv style={{ whiteSpace: "pre-wrap" }}>
      {label}
    </FInter.SmallTextDiv>
  </MagicBox>
);

export const Checkbox = styled(MagicBoxRow)`
  width: 16px !important;
  height: 16px !important;
`;

const CookiesWarningWrapper = styled(FlexBox)`
  position: fixed;
  z-index: 299999;
  bottom: 40px;
  left: 50%;
  padding: 0;
  width: 100%;
  transform: translateX(-50%);
  max-width: 700px;
  background-color: ${(p) => p.theme.cookies.bg};
  border-radius: 3px;
  flex-flow: column;
  justify-content: center;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  @media (max-width: 992px) {
    max-width: 553px;
  }
  @media (max-width: 743px) {
    max-width: 300px;
  }
`;

const CookiesInner = styled(FlexBox)<{ isDesign?: boolean }>`
  position: relative;
  padding: 11px 44px 11px 12px;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  color: ${({theme}) => theme.cookies.mainColor
};

  a {
    color: #909090;
  }

  .legals {
    text-decoration: underline;
    font-family: "Inter";
    font-weight: 400;
    cursor: pointer;
    color: #007bff;
  }

  @media (max-width: 743px) {
    padding: 8px 32px 8px 8px;
  }
`;

const CloseButton = styled.span`
  position: absolute;
  right: 15px;
  top: 15px;
  cursor: pointer;

  &:hover {
    svg path {
      fill: ${({ theme }) => theme.cookies.icon.hoverFill};
      transition: fill 0.2s;
    }
  }

  svg path {
    fill: ${({ theme }) => theme.cookies.icon.fill};
    transition: fill 0.2s;
  }

  @media (max-width: 743px) {
    top: 3px;
    right: 10px;
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const CookiesText = styled.span`
  text-align: start;
  &, a {
    display: inline;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 0;
    
    @media (max-width: 992px) {
      font-size: 13px;
      line-height: 16px;
      letter-spacing: 0;
    }

    @media (max-width: 743px) {
        font-size: 11px;
        line-height: 13px;
        letter-spacing: 0;
    }
  }
`;

const ShowMoreText = styled.div<{expanded?: boolean}>`
  ${p => !p.expanded?
    'display: inline-block;'
    :
    css`
      display: flex;
      flex-direction: column;
      justify-content: start;
    `
  }
  width: 100%;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: 0;
  margin-bottom: 0;
  
  @media (max-width: 992px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }
  @media (max-width: 743px) {
      font-size: 11px;
      line-height: 13px;
      letter-spacing: 0;
  }
`

const ShowMoreLessButton = styled.div`
  color: #909090;
  font-size: inherit;
  line-height: inherit;
  display: inline;
  cursor: pointer;
`

enum CookieType {
  HSE = "HSE",
  Deziiiign = "Deziiiign",
  None = "None",
}

interface CookieContext {
  type: CookieType;
  isCurrentContext: boolean[];
}

const CookiesWarning: FC = () => {
  const { t } = useTranslation();
  const warningSeen = getCookie("cookies_warning_seen");
  const is744 = useBreakpoint(LayoutConst.screen.s - 1);
  const [expanded, setExpanded] = useState(false)

  const [visible, setVisible] = useState(!warningSeen);

  const onClose = () => {
    setVisible(false);
    setCookie("cookies_warning_seen", true);
  };

  const {
    isHSEDesign,
    isDeziiignMain,
    isMediiia,
    isGeoMain,
    isSuperPuper,
    isAcademy,
    isBrnd,
    isDvaDesign,
    isSchool,
    isOnlines,
    isOnlineHSEDesign,
    isGalllleryArt,
    isAgeytomesh,
    isGeoDafes
  } = useAppTester();

  const cookieContextList: CookieContext[] = useMemo(
    () => [
      { type: CookieType.HSE, isCurrentContext: [isHSEDesign, isAcademy] },
      {
        type: CookieType.Deziiiign,
        isCurrentContext: [
          isDeziiignMain,
          isMediiia,
          isGeoMain,
          isSuperPuper,
          isBrnd,
          isDvaDesign,
          isSchool,
          isOnlines,
          isOnlineHSEDesign,
          isGalllleryArt,
          isAgeytomesh,
          isGeoDafes
        ],
      },
    ],
    [
      isHSEDesign,
      isDeziiignMain,
      isMediiia,
      isGeoMain,
      isSuperPuper,
      isAcademy,
      isSchool,
      isOnlines,
      isOnlineHSEDesign,
      isGalllleryArt,
      isAgeytomesh,
      isGeoDafes
    ]
  );

  const cookieType = useMemo(() => {
    return (
      cookieContextList.find((item) =>
        item.isCurrentContext.some((context) => context)
      )?.type || CookieType.None
    );
  }, [cookieContextList]);

  const cookText = useMemo(() => {
    if (cookieType === CookieType.Deziiiign) {
      return (
        expanded?
        <CookiesText className="first_child last_child ">
          {t("text.cookiesUsage")}&nbsp;
          <a
            target="_blank"
            href={`${process.env.REACT_APP_URL_DEZIIIGN}/legals/privacy`}
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          , {t("text.cookiesRules")} –&nbsp;
          <a
            target="_blank"
            href={`${process.env.REACT_APP_URL_DEZIIIGN}/legals/privacy`}
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          . {t("text.cookiesInfo")}
        </CookiesText>
        :
        <CookiesText>
          {t(is744? 'text.cookiesUsageCroppedMobile' : 'text.cookiesUsageCropped')}
        </CookiesText>
      );
    } else {
      return (
        expanded?
        <CookiesText className="first_child last_child ">
          {t("text.cookiesUsageHSE")}&nbsp;
          <a
            target="_blank"
            href="https://www.hse.ru/cookie.html"
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          , {t("text.cookiesRules")} –&nbsp;
          <a
            target="_blank"
            href="https://www.hse.ru/data_protection_regulation"
            title={t("text.cookiesForward")}
            className="link"
          >
            {t("text.cookiesLink")}
          </a>
          . {t("text.cookiesInfoHSE")}
        </CookiesText>
        :
        <CookiesText>
          {t(is744? 'text.cookiesUsageHSECroppedMobile' : 'text.cookiesUsageHSECropped')}
        </CookiesText>
      );
    }
  }, [cookieType, expanded, t, is744]);


  if (!visible || cookieType === CookieType.None) {
    return null;
  }

  return (
    <CookiesWarningWrapper>
      <CookiesInner>
        <ShowMoreText expanded={expanded}>
          {cookText}
          <ShowMoreLessButton onClick={() => setExpanded(!expanded)}>
          {
            expanded?
              ` ${t("show_less_text")}`
              :
              ` ${t("show_more_text")}`
          }
        </ShowMoreLessButton>
        </ShowMoreText>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </CookiesInner>
    </CookiesWarningWrapper>
  );
};

export default CookiesWarning;
